import { useEffect, useState } from 'react'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { setValueInLocation } from '../../../Services/Utils'

const defaultTheme = {
  colors: {
    white: '#ffffff',
    black: '#000000',
    translucent: 'rgba(255, 255, 255, 0.55)',
    opaque: 'rgba(255, 255, 255, 0.8)',
    transparent: 'rgba(0, 0, 0, 0)',
    grey: '#d3d3d3',
    themeColor: '#5F991F',
    darkTransparent: 'rgba(0,0,0,0.5)',
    fontColor: '#32324e',
    textButtonColor: '#ffffff'
  },
  logoImage: 'https://s3.eu-central-1.amazonaws.com/sign.siap/a48e40306c357bb190808e532b1a3d23929df5025b171d23e0f48c61fa01a872.png'
}

export const EMAIL_PROVIDER_OPTIONS = [
  { label: 'SMTP (username / password)', value: 'smtp' },
  { label: 'Microsoft 365 (OAuth2 SMTP)', value: 'microsoft_oauth' }
]

export const MICROSOFT_EMAIL_DEFAULTS = {
  host: 'smtp.office365.com',
  port: 587
}

const getNestedValue = (obj, location) =>
  location.split('.').reduce((acc, key) => acc?.[key], obj)

export const getEmailConfigDisplayValue = (location, configEmail = {}) => {
  const stored = getNestedValue(configEmail, location.replace(/^configEmail\./, ''))
  if (stored !== undefined && stored !== null && stored !== '') return stored

  const provider = configEmail.provider || 'smtp'
  if (provider !== 'microsoft_oauth') return ''

  if (location === 'configEmail.auth.user') return configEmail.from ?? ''
  if (location === 'configEmail.host') return MICROSOFT_EMAIL_DEFAULTS.host
  if (location === 'configEmail.port') return MICROSOFT_EMAIL_DEFAULTS.port
  return ''
}

export const applyMicrosoftEmailDefaults = (configEmail = {}) => {
  if ((configEmail.provider || 'smtp') !== 'microsoft_oauth') return configEmail

  const auth = { ...configEmail.auth }
  if (!auth.user && configEmail.from) auth.user = configEmail.from

  return {
    ...configEmail,
    auth,
    host: configEmail.host || MICROSOFT_EMAIL_DEFAULTS.host,
    port: configEmail.port || MICROSOFT_EMAIL_DEFAULTS.port
  }
}

export const useEnvironment = (environmentId) => {
  const [environment, setEnvironment] = useState()
  const { data, isSuccess } = useCQuery(['environmentById', environmentId])

  useEffect(() => {
    if (!isSuccess) return
    setEnvironment({
      ...data,
      configEmail: {
        provider: 'smtp',
        ...data?.configEmail
      },
      appTheme: { ...defaultTheme, ...data?.appTheme },
      ssoKey: data?.ssoKey || Math.random().toString(36).slice(2)
    })
  }, [isSuccess, data])

  const setEnvironmentProp = (prop, value) => {
    const newEnvironment = setValueInLocation({ ...environment }, prop, value)
    setEnvironment(newEnvironment)
  }

  return [environment, setEnvironmentProp, isSuccess]
}

export const saveEnvironment = async (environment, isNew) => {
  if (isNew) {
    try {
      const res = await FC.service('environments').create({ ...environment })
      invalidateQuery(['environment', 'environments'])
      window.growl.show({ severity: 'success', summary: 'Success', detail: 'Salvataggio effettuato' })
      return res
    } catch (e) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore Salvataggio' })
      return false
    }
  }
  try {
    const res = await FC.service('environments').patch(environment._id, environment)
    invalidateQuery(['environment', 'environments'])
    window.growl.show({ severity: 'success', summary: 'Success', detail: 'Salvataggio effettuato' })
    return res
  } catch (e) {
    process.env.NODE_ENV === 'development' && console.log(e)
    window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore Salvataggio' })
    return false
  }
}

const validateEmailConfigForTest = (config, log) => {
  const provider = config?.provider || 'smtp'
  if (!config?.from) {
    log('❌ Mittente (from) obbligatorio.')
    return false
  }
  if (provider === 'microsoft_oauth') {
    const { oauth = {} } = config
    if (!oauth.tenantId || !oauth.clientId || !oauth.clientSecret) {
      log('❌ Dati Microsoft OAuth incompleti (tenantId, clientId, clientSecret).')
      return false
    }
    if (!oauth.refreshToken) {
      log('❌ Refresh Token mancante.')
      return false
    }
    return true
  }
  if (!config?.host || !config?.auth?.user || !config?.auth?.pass) {
    log('❌ Dati SMTP incompleti (host, username, password).')
    return false
  }
  return true
}

/**
 * Test invio email (SMTP classico o Microsoft OAuth2 SMTP).
 * Destinatario: campo "Email destinatario" del test, altrimenti mittente (from).
 * @param {Object} config - environment.configEmail
 * @param {string} [toOverride] - Email destinatario test (configEmail.to in UI)
 * @param {Function} [setLog] - Callback log in UI
 */
export const testEmailConfig = async (config, toOverride, setLog) => {
  const log = (msg) => setLog && setLog(msg)

  if (!validateEmailConfigForTest(config, log)) return

  const testTo = toOverride || config.to || config.from
  const provider = config?.provider || 'smtp'

  const message = {
    text: `Test invio email Firma Elettronica (${provider})`,
    from: config.from,
    to: testTo,
    subject: 'Test invio email Firma Elettronica'
  }

  log(`📧 Destinatario test: ${testTo}`)
  log('⏳ Test in corso...')

  try {
    const res = await FC.service('sendMail').create({ config, message })

    if (!res.error) {
      const { response } = res
      if (response?.startsWith?.('250')) {
        log(`✅ Invio riuscito\n${response}`)
      } else {
        log(`⚠️ Risposta non standard:\n${response}`)
      }
      return res
    }

    log(`❌ Errore invio:\n${res.error.message || JSON.stringify(res.error, null, 2)}`)
  } catch (err) {
    console.error('❌ Errore generale durante il test email:', err)
    log(`❌ Errore di rete o backend:\n${err.message || JSON.stringify(err)}`)
  }
}

/** @deprecated use testEmailConfig */
export const testSMTP = testEmailConfig

export const authorizeMicrosoft365 = async (environment, setAuthLog, setEnvironment) => {
  const log = (msg) => setAuthLog && setAuthLog(msg)
  const config = applyMicrosoftEmailDefaults(environment?.configEmail)
  const { oauth = {} } = config

  if (!environment?._id || environment._id === 'new') {
    log('❌ Salvare l\'environment prima di autorizzare Microsoft 365.')
    return
  }
  if (!config.from) {
    log('❌ Mittente (from) obbligatorio prima dell\'autorizzazione.')
    return
  }
  if (!oauth.tenantId || !oauth.clientId || !oauth.clientSecret) {
    log('❌ Compilare Tenant ID, Client ID e Client Secret prima di autorizzare.')
    return
  }

  log('⏳ Avvio autorizzazione Microsoft 365...')

  try {
    await FC.service('environments').patch(environment._id, {
      configEmail: applyMicrosoftEmailDefaults(environment.configEmail)
    })

    const start = await FC.service('sendMail').create({
      action: 'microsoftOAuthStart',
      config: { oauth: { tenantId: oauth.tenantId, clientId: oauth.clientId, clientSecret: oauth.clientSecret } }
    })

    log(
      `${start.message || 'Apri il link e inserisci il codice.'}\n\n` +
      `Link: ${start.verificationUri}\n` +
      `Codice: ${start.userCode}\n\n` +
      'In attesa di autorizzazione...'
    )

    let intervalMs = (start.interval || 5) * 1000
    const maxAttempts = Math.ceil((start.expiresIn || 900) / (start.interval || 5))

    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs))
      const poll = await FC.service('sendMail').create({
        action: 'microsoftOAuthPoll',
        environmentId: environment._id,
        config: { oauth: { tenantId: oauth.tenantId, clientId: oauth.clientId, clientSecret: oauth.clientSecret } },
        deviceCode: start.deviceCode
      })

      if (poll.status === 'pending') {
        if (poll.interval) intervalMs = poll.interval * 1000
        continue
      }

      if (poll.status === 'success') {
        setEnvironment('configEmail.oauth.refreshToken', poll.refreshToken)
        invalidateQuery(['environment', 'environments'])
        log('✅ Autorizzazione completata.')
        window.growl.show({ severity: 'success', summary: 'Success', detail: 'Microsoft 365 autorizzato' })
        return
      }
    }

    log('❌ Tempo scaduto. Riprovare autorizzazione.')
  } catch (err) {
    log(`❌ ${err.message || 'Errore durante l\'autorizzazione Microsoft 365.'}`)
  }
}
